/**
 * NMS Storefront API Client
 *
 * Centralized client for Shopify Storefront API requests
 * Provides caching, request deduplication, and error handling
 *
 * Built following NMS JavaScript standards - singleton pattern
 */

class NMSStorefrontClient extends window.NMSBaseClass {
  constructor() {
    super();

    // Request deduplication map
    this.pendingRequests = new Map();

    // API configuration
    this.storeDomain = window.Shopify?.shop || 'shop.myshopify.com';
    this.apiVersion = '2023-10';
    this.endpoint = `https://${this.storeDomain}/api/${this.apiVersion}/graphql.json`;

    // Get access token from global data or fallback
    this.accessToken = this.getGlobalData('storefrontAccessToken') || '';

    // Product query template
    this.productQuery = `
      query getProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          handle
          title
          description
          availableForSale
          images(first: 1, reverse: true) {
            edges {
              node {
                id
                url
                altText
                width
                height
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          options {
            id
            name
            values
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions {
                  name
                  value
                }
                priceV2 {
                  amount
                  currencyCode
                }
                compareAtPriceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;
  }

  /**
   * Set the access token for API requests
   * @param {string} token - Storefront access token
   */
  setAccessToken(token) {
    this.accessToken = token;
  }

  /**
   * Set the store domain for API calls
   * @param {string} domain - Store domain (e.g., 'store.com')
   */
  setStoreDomain(domain) {
    if (domain) {
      this.storeDomain = domain;
      this.endpoint = `https://${this.storeDomain}/api/${this.apiVersion}/graphql.json`;
    }
  }

  /**
   * Fetch product data by handle with deduplication and caching
   * @param {string} handle - Product handle
   * @returns {Promise<Object>} Transformed product data
   */
  async fetchProductByHandle(handle) {
    // Check if request is already pending
    if (this.pendingRequests.has(handle)) {
      return this.pendingRequests.get(handle);
    }

    // Create promise for this request
    const requestPromise = this.#executeProductFetch(handle);

    // Store in pending requests
    this.pendingRequests.set(handle, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(handle);
    }
  }

  /**
   * Execute the actual product fetch
   * @param {string} handle - Product handle
   * @returns {Promise<Object>} Transformed product data
   * @private
   */
  async #executeProductFetch(handle) {
    // Check cache first
    const cacheKey = `product:${handle}`;
    const cached = this.cache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      // Build request body
      const requestBody = JSON.stringify({
        query: this.productQuery,
        variables: { handle },
      });

      // Use native fetch instead of base class method for GraphQL
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.accessToken,
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Validate response
      if (data.errors && data.errors.length > 0) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (!data.data || !data.data.product) {
        throw new Error(`Product not found: ${handle}`);
      }

      // Transform to internal format
      const transformedData = this.#transformProductData(data.data.product);

      // Cache the transformed result
      this.cache.set(cacheKey, transformedData);

      return transformedData;
    } catch (error) {
      const message = this.handleError(error, `Failed to fetch product: ${handle}`);
      throw new Error(message);
    }
  }

  /**
   * Transform GraphQL product data to internal format
   * @param {Object} product - GraphQL product data
   * @returns {Object} Transformed product data
   * @private
   */
  #transformProductData(product) {
    const variants = product.variants.edges.map(edge => {
      const variant = edge.node;
      const price = this.#centsFromAmount(variant.priceV2.amount);
      const compareAtPrice = variant.compareAtPriceV2 ? this.#centsFromAmount(variant.compareAtPriceV2.amount) : null;

      return {
        id: this.#extractId(variant.id),
        title: variant.title,
        price: price,
        compare_at_price: compareAtPrice,
        available: variant.availableForSale,
        option1: variant.selectedOptions[0]?.value || null,
        option2: variant.selectedOptions[1]?.value || null,
        option3: variant.selectedOptions[2]?.value || null,
      };
    });

    const minPrice = this.#centsFromAmount(product.priceRange.minVariantPrice.amount);
    const maxPrice = this.#centsFromAmount(product.priceRange.maxVariantPrice.amount);
    const compareAtMinPrice = product.compareAtPriceRange.minVariantPrice
      ? this.#centsFromAmount(product.compareAtPriceRange.minVariantPrice.amount)
      : null;

    return {
      id: this.#extractId(product.id),
      handle: product.handle,
      title: product.title,
      available: product.availableForSale,
      featured_media:
        product.images.edges.length > 0
          ? {
              alt: product.images.edges[0].node.altText,
              url: product.images.edges[0].node.url,
              width: product.images.edges[0].node.width,
              height: product.images.edges[0].node.height,
            }
          : null,
      price_min: minPrice,
      price_max: maxPrice,
      compare_at_price_min: compareAtMinPrice,
      variants: variants,
      options: product.options.map((option, index) => ({
        name: option.name,
        position: index + 1,
        values: option.values,
      })),
    };
  }

  /**
   * Convert decimal amount to cents
   * @param {string} amount - Decimal amount string
   * @returns {number} Amount in cents
   * @private
   */
  #centsFromAmount(amount) {
    return parseInt(parseFloat(amount) * 100);
  }

  /**
   * Extract numeric ID from Shopify global ID
   * @param {string} globalId - Shopify global ID (e.g., "gid://shopify/Product/123")
   * @returns {string} Numeric ID
   * @private
   */
  #extractId(globalId) {
    return globalId.split('/').pop();
  }

  /**
   * Clear cache for a specific product or all products
   * @param {string} [handle] - Product handle to clear, or undefined for all
   */
  clearCache(handle) {
    if (handle) {
      this.cache.delete(`product:${handle}`);
    } else {
      // Clear all product cache entries
      for (const key of this.cache.keys()) {
        if (key.startsWith('product:')) {
          this.cache.delete(key);
        }
      }
    }
  }
}

// Create singleton instance
window.neemans = window.neemans || {};
window.neemans.storefrontClient = window.neemans.storefrontClient || new NMSStorefrontClient();

// Export for modules
window.NMSStorefrontClient = NMSStorefrontClient;
